// #3312 LINK.A2 — the one definition of the change-order vocabulary.
//
// A change order's type, status and cost impact live as strings inside the
// stringified `artefact_versions.notes` JSON, not in a DB enum, so nothing but
// this module stops the backend validators, the agent parser, the prompts and
// the web labels from drifting apart.

/** Every CO type, including the three that only an inbound finding produces. */
export const CO_TYPES = [
  'SPIKE', 'COMPLEXITY_INCREASE', 'REQUIREMENTS_GAP', 'MANUAL',
  'SECURITY_FINDING', 'INCIDENT', 'DEFECT',
] as const;
export type CoType = (typeof CO_TYPES)[number];

/** The types a person (or the AI agent) may pick when raising a CO by hand. */
export const CO_MANUAL_TYPES = ['SPIKE', 'COMPLEXITY_INCREASE', 'REQUIREMENTS_GAP', 'MANUAL'] as const;
export type CoManualType = (typeof CO_MANUAL_TYPES)[number];

export const CO_STATUSES = [
  'DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'DEFERRED', 'DISMISSED', 'RESOLVED',
] as const;
export type CoStatus = (typeof CO_STATUSES)[number];

/**
 * #3710 — the ONLY statuses a CLIENT may see. A positive allow-list: any other
 * status (DRAFT, DISMISSED, RESOLVED, a value nobody defined) is hidden.
 * RESOLVED is excluded for now (Q-C): its first writer (A3c, #3516) decides.
 */
export const CO_CLIENT_VISIBLE_STATUSES = ['SUBMITTED', 'APPROVED', 'REJECTED', 'DEFERRED'] as const;

/** The decisions a client can respond with (the subset of statuses that `respond` writes). */
export const CO_RESPOND_DECISIONS = ['APPROVED', 'REJECTED', 'DEFERRED'] as const;
export type CoRespondDecision = (typeof CO_RESPOND_DECISIONS)[number];

export const CO_COST_IMPACTS = ['NONE', 'INCLUDED', 'ADDITIONAL'] as const;
export type CoCostImpact = (typeof CO_COST_IMPACTS)[number];

// Must equal the CHECK lists of migration 0256 (inbound_findings_source_check,
// inbound_findings_severity_check) — pinned by changeOrders.test.ts.
export const FINDING_SOURCES = ['MANUAL', 'UPLOAD', 'WEBHOOK', 'GITHUB', 'SLACK'] as const;
export type FindingSource = (typeof FINDING_SOURCES)[number];

export const FINDING_SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'] as const;
export type FindingSeverity = (typeof FINDING_SEVERITIES)[number];

// Lower-cased, trimmed category → CO type. Anything not listed is a DEFECT.
// A Map, not an object literal: an object lookup resolves '__proto__', 'constructor', 'toString'… through the prototype chain.
const CATEGORY_TO_CO_TYPE: ReadonlyMap<string, CoType> = new Map<string, CoType>([
  ['security', 'SECURITY_FINDING'],
  ['vulnerability', 'SECURITY_FINDING'],
  ['vuln', 'SECURITY_FINDING'],
  ['cve', 'SECURITY_FINDING'],
  ['security-finding', 'SECURITY_FINDING'],
  ['security finding', 'SECURITY_FINDING'],
  ['incident', 'INCIDENT'],
  ['outage', 'INCIDENT'],
  ['alert', 'INCIDENT'],
  ['downtime', 'INCIDENT'],
]);

/** Map a finding's free-text category to the CO type its draft gets. Empty/null/unknown → DEFECT. */
export function findingCategoryToCoType(category: string | null | undefined): CoType {
  const key = (category ?? '').trim().toLowerCase();
  return CATEGORY_TO_CO_TYPE.get(key) ?? 'DEFECT';
}
